<script setup lang="ts">
// import CreateIranianBankAccountModal from '../../views/requests/partials/CreateIranianBankAccountModal.vue';
// import { BANK_ACCOUNTS_QUERY_KEYS } from '@/composables/useBankAccountsQueries.ts';
import { useQueryClient } from '@tanstack/vue-query'
import { onClickOutside } from '@vueuse/core'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { IranianBankAccountFromAPI } from '@/api/typings/bank-account.typing'

interface Props {
  modelValue: string | number | null
  label?: string
  placeholder?: string
  disabled?: boolean
  error?: string
  accounts?: IranianBankAccountFromAPI[]
  countries: Array<{ label: string; value: string | number }>
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  label: 'حساب مقصد ریالی',
  modelValue: '',
  accounts: () => [],
  countries: () => [],
})

const emit = defineEmits<(e: 'update:modelValue', value: string | number) => void>()

const queryClient = useQueryClient()

const isCreateAccountModalOpen = ref(false)
const isOpen = ref(false)
const wrapper = ref<HTMLElement | null>(null)
const loading = ref(false)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const selectedOption = ref<any>(null)

onClickOutside(wrapper, () => {
  isOpen.value = false
})

const findAndSetSelectedAccount = () => {
  if (!props.modelValue) {
    selectedOption.value = null
    return
  }

  if (!props.accounts || props.accounts.length === 0) {
    return
  }

  const account = props.accounts.find((acc) => acc.id === props.modelValue)

  if (account) {
    const displayNumber = account.cardNumber || account.shebaNumber || 'شماره نامشخص'
    selectedOption.value = {
      id: account.id,
      label: `${account.bankName} - ${displayNumber}`,
      bankName: account.bankName,
      cardNumber: account.cardNumber,
      shebaNumber: account.shebaNumber,
      ownerFullName: account.ownerFullName,
    }
  } else {
    selectedOption.value = null
  }
}

const updateValue = (value: string | number) => {
  if (value === 'create_new') {
    openCreateAccountModal()
    return
  }

  if (value === 'verify_accounts') {
    window.open('/profile/destination-bank-accounts', '_blank')
    return
  }

  emit('update:modelValue', value)

  const account = props.accounts.find((acc) => acc.id === value)

  if (account) {
    const displayNumber = account.cardNumber || account.shebaNumber || 'شماره نامشخص'
    selectedOption.value = {
      id: account.id,
      label: `${account.bankName} - ${displayNumber}`,
      bankName: account.bankName,
      cardNumber: account.cardNumber,
      shebaNumber: account.shebaNumber,
      ownerFullName: account.ownerFullName,
    }
  }

  isOpen.value = false
}

const openCreateAccountModal = () => {
  isCreateAccountModalOpen.value = true
  isOpen.value = false
}

// const handleCreateAccountConfirm = () => {
//     isCreateAccountModalOpen.value = false;
//     queryClient.invalidateQueries({ queryKey: [...BANK_ACCOUNTS_QUERY_KEYS.iranian] });
// }

const toggleOpen = () => {
  if (!props.disabled) {
    isOpen.value = !isOpen.value
  }
}

watch(
  () => props.modelValue,
  (newVal) => {
    setTimeout(() => {
      findAndSetSelectedAccount()
    }, 100)
  }
)

watch(
  () => props.accounts,
  (newAccounts) => {
    setTimeout(() => {
      findAndSetSelectedAccount()
    }, 100)
  },
  { deep: true, immediate: true }
)
</script>

<template>

    <div class="relative flex flex-col gap-1 w-full text-[#1A1A1A] text-[13px] font-medium "
        :class="props.disabled ? 'cursor-not-allowed opacity-[0.5]' : ''" ref="wrapper">
        <!-- Label -->

        <label v-if="label"
            class="w-fit bg-white px-1 transition-all z-30 duration-400 text-[#6C6C6C] pointer-events-none" :class="(isOpen || selectedOption)
                ? 'absolute ms-4 -mt-2 text-[10px] font-bold'
                : 'absolute mt-[13px] ms-5 text-[13px] font-medium'">
            {{ label }}
        </label>

        <!-- Custom Select Wrapper -->

        <div class="relative w-full h-[48px]" :class="props.disabled ? 'cursor-not-allowed opacity-[0.5]' : ''">
            <!-- Selected Value Display -->

            <div class="w-full h-full rounded-xl border px-[18px] pr-8 font-medium outline-none transition
                 border-[#E0E0E0] focus:outline-none focus:ring-0 focus:border-[#000]
                 disabled:bg-gray-100 disabled:cursor-not-allowed bg-white flex items-center" :class="[
                    props.disabled ? 'cursor-not-allowed' : 'cursor-pointer '
                ]" @click="toggleOpen">
                <span class="flex items-center gap-2">
                    <template v-if="selectedOption">
                        {{ selectedOption.label }}
                    </template>

                    <template v-else>
                        انتخاب کنید
                    </template>
                </span>
            </div>

            <!-- Chevron Icon -->

            <img src="/svg/chevron-down.svg" alt="chevron"
                class="absolute top-1/2 end-0.5 -translate-y-1/2 w-6 h-6 pointer-events-none transition-transform duration-200"
                :class="{ 'rotate-180': isOpen }" />

            <!-- Custom Dropdown Options -->

            <div v-if="isOpen"
                class="absolute top-full pt-2 left-0 right-0 mt-1 bg-white border border-[#E0E0E0] rounded-xl shadow-lg z-[999] max-h-64 overflow-y-auto custom-dropdown">
                <div v-if="loading" class="px-4 py-3 text-center">
                    در حال بارگذاری...
                </div>

                <div v-else-if="props.accounts.length > 0">
                    <label v-for="account in props.accounts" :key="String(account.id)"
                        class="px-4 py-4 cursor-pointer transition-all duration-200 font-medium flex items-center gap-3 hover:bg-[#f8f9fa]">
                        <input type="radio" name="destination-bank-account" :value="account.id"
                            :checked="account.id === props.modelValue" @change="updateValue(account.id ?? '')"
                            class="w-4 h-4 accent-[#2626BF] shrink-0" />

                        <img src="/svg/bankIcon.svg" />

                        <div class="flex flex-col font-medium gap-1 font-fa">
                            <span class="text-[#1A1A1A] text-sm">
                                {{ account.bankName }}
                            </span>

                            <span class="text-[#6C6C6C] text-xs font-sans">
                                {{ (account.cardNumber || account.shebaNumber)?.replace(/(.{4})/g, '$1 ') }}
                            </span>
                        </div>
                    </label>
                </div>

                <div v-else class="px-4 py-3 text-center">
                    حسابی یافت نشد
                </div>

                <!-- حساب جدید -->

                <div @click="openCreateAccountModal"
                    class="px-4 py-3 cursor-pointer transition-all duration-200 text-[#0E59FA] flex items-center gap-2 hover:bg-[#f8f9fa]">
                    <span class="text-xl font-thin pt-1">
                        +
                    </span>

                    حساب جدید
                </div>
            </div>
        </div>

        <!-- Create Bank Account Drawer -->
        <CreateBankAccountDrawer v-model="isCreateAccountModalOpen" :countries="countries" />

        <!-- Error -->

        <div v-if="error" class="flex items-center gap-1 text-[#ed145b] mt-1">
            <img src="/svg/error-icon.svg" width="16" alt="error-icon" />

            {{ error }}
        </div>
    </div>
</template>

<style scoped>
.custom-dropdown::-webkit-scrollbar {
    width: 6px;
}

.custom-dropdown::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.custom-dropdown::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.custom-dropdown::-webkit-scrollbar-thumb:hover {
    background: #a1a1a1;
}
</style>